// TenantLifecyclePage — Tenant / Company Data Lifecycle console.
//
// This is NOT a "delete tenant" screen. It surfaces where each tenant sits in the
// data-lifecycle (offboarding, retention review, anonymization eligibility) and makes
// every destructive-adjacent transition deliberate: reason-gated, dependency-aware,
// and — because the backend transition endpoints are still stubs — intent-only.
//
// SAFETY MODEL:
//  - No simple "delete tenant" action exists.
//  - Hard deletion / anonymization are DISABLED (feature-flag gated).
//  - A legal hold (or payroll records / open billing / active employees) blocks
//    anonymization and deletion; the UI disables those actions and explains why.
//  - Every transition routes through <ConfirmDangerDialog> and requires a reason.
//  - Transition endpoints are stubbed: actions record intent only and never claim
//    the transition happened.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  message,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  ExperimentOutlined,
  LockOutlined,
  PauseCircleOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { getTenantLifecycles, type TenantLifecycle } from '@/services/dataGovernanceService';
import {
  DryRunBanner,
  ComplianceStatusBadge,
  LegalHoldBadge,
  ConfirmDangerDialog,
} from '@/components/compliance';
import { useAppSelector } from '@/app/hooks';
import { selectHasPermission } from '@/features/auth/authSlice';

// The set of reason-gated transitions we can currently express as "recorded intent".
type TransitionKey =
  | 'suspend'
  | 'offboarding'
  | 'retention_dry_run'
  | 'retention_review_complete';

interface TransitionSpec {
  key: TransitionKey;
  label: string;
  icon: React.ReactNode;
  danger?: boolean;
  title: string;
  description: React.ReactNode;
  warning?: string;
  confirmLabel: string;
  // dry-run is server-side and non-destructive; it gets a lighter info toast.
  dryRun?: boolean;
}

const TRANSITIONS: TransitionSpec[] = [
  {
    key: 'suspend',
    label: 'Suspend tenant',
    icon: <PauseCircleOutlined />,
    title: 'Suspend tenant',
    description:
      'Suspending pauses tenant access but preserves all data. This does not delete or anonymize anything.',
    confirmLabel: 'Suspend tenant',
  },
  {
    key: 'offboarding',
    label: 'Start offboarding',
    icon: <StopOutlined />,
    danger: true,
    title: 'Start offboarding',
    description:
      'Moves the tenant into an OFFBOARDING state so retention review can begin. No records are removed by this step.',
    warning: 'Offboarding begins a retention lifecycle. Data is retained until review completes.',
    confirmLabel: 'Start offboarding',
  },
  {
    key: 'retention_dry_run',
    label: 'Run tenant retention dry-run',
    icon: <ExperimentOutlined />,
    title: 'Run tenant retention dry-run',
    description:
      'Evaluates what retention enforcement WOULD do for this tenant. Runs server-side and is strictly non-destructive — nothing is deleted or anonymized.',
    confirmLabel: 'Run dry-run',
    dryRun: true,
  },
  {
    key: 'retention_review_complete',
    label: 'Mark retention review complete',
    icon: <SafetyCertificateOutlined />,
    title: 'Mark retention review complete',
    description:
      'Records that a human has reviewed this tenant\'s retention obligations. This is a prerequisite for scheduling anonymization; it does not itself anonymize anything.',
    confirmLabel: 'Mark reviewed',
  },
];

const yesNoTag = (v: boolean) =>
  v ? <Tag color="volcano">Yes</Tag> : <Tag color="green">No</Tag>;

const TenantLifecyclePage: React.FC = () => {
  const canView = useAppSelector(selectHasPermission('tenant_lifecycle:view'));
  const canUpdate = useAppSelector(selectHasPermission('tenant_lifecycle:update'));

  const [rows, setRows] = useState<TenantLifecycle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TenantLifecycle | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Active reason-gated confirmation (one at a time within the drawer).
  const [pending, setPending] = useState<TransitionSpec | null>(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await getTenantLifecycles();
        if (alive) setRows(data);
      } catch (e: any) {
        if (alive) message.error(e?.response?.data?.message || 'Could not load tenant lifecycle.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [canView]);

  const openDrawer = (t: TenantLifecycle) => {
    setSelected(t);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
    setPending(null);
  };

  // Blocking dependencies gate anonymization & deletion.
  const blockers = useMemo(() => {
    if (!selected) return [] as string[];
    const b: string[] = [];
    if (selected.legal_hold_active) b.push('an active legal hold');
    if (selected.has_payroll_records) b.push('payroll records');
    if (selected.has_open_billing) b.push('open billing');
    if (selected.active_employees > 0) b.push(`${selected.active_employees} active employee(s)`);
    return b;
  }, [selected]);

  const anonymizationBlocked = blockers.length > 0;

  // NO-OP transition handler — records intent only; never claims the change happened.
  const runTransition = async (spec: TransitionSpec, _reason: string) => {
    setActing(true);
    try {
      // Backend transition endpoints are stubs; there is nothing to call yet.
      await Promise.resolve();
      if (spec.dryRun) {
        message.info('Retention dry-run is evaluated server-side — no changes were made.');
      } else {
        message.info('Lifecycle transition endpoint pending — recorded intent only');
      }
    } finally {
      setActing(false);
      setPending(null);
    }
  };

  const columns: ColumnsType<TenantLifecycle> = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_name',
      key: 'tenant_name',
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: TenantLifecycle['status']) => (
        <ComplianceStatusBadge kind="tenant_lifecycle" value={status} />
      ),
    },
    {
      title: 'Legal hold',
      dataIndex: 'legal_hold_active',
      key: 'legal_hold_active',
      render: (active: boolean) => <LegalHoldBadge active={active} />,
    },
    {
      title: 'Active employees',
      dataIndex: 'active_employees',
      key: 'active_employees',
      align: 'right',
    },
    {
      title: 'Payroll records',
      dataIndex: 'has_payroll_records',
      key: 'has_payroll_records',
      render: yesNoTag,
    },
    {
      title: 'Open billing',
      dataIndex: 'has_open_billing',
      key: 'has_open_billing',
      render: yesNoTag,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (v: string) => (v ? fmtDate(v) : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      render: (_: unknown, record: TenantLifecycle) => (
        <Button size="small" onClick={() => openDrawer(record)}>
          Manage
        </Button>
      ),
    },
  ];

  if (!canView) {
    return (
      <Card>
        <Alert
          type="error"
          showIcon
          message="Access denied"
          description="You do not have permission to view the tenant data lifecycle."
        />
      </Card>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 4 }}>Tenant Data Lifecycle</h2>
        <div style={{ color: 'rgba(0,0,0,0.55)' }}>
          Offboarding, retention review, and anonymization eligibility — controlled, not a delete
          button.
        </div>
      </div>

      <DryRunBanner />

      <Card style={{ marginTop: 16 }} bodyStyle={{ padding: 0 }}>
        <Table<TenantLifecycle>
          rowKey="tenant_id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No tenants in a managed lifecycle state. Backend endpoint pending."
              />
            ),
          }}
        />
      </Card>

      <Drawer
        title={selected ? `Manage — ${selected.tenant_name}` : 'Manage tenant'}
        width={560}
        open={drawerOpen}
        onClose={closeDrawer}
        destroyOnClose
      >
        {selected && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Data-category summary */}
            <Row gutter={12}>
              <Col span={12}>
                <Card size="small">
                  <Statistic title="Active employees" value={selected.active_employees} />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Legal hold"
                    value={selected.legal_hold_active ? 'Active' : 'None'}
                    prefix={selected.legal_hold_active ? <LockOutlined /> : undefined}
                    valueStyle={selected.legal_hold_active ? { color: '#d4380d' } : undefined}
                  />
                </Card>
              </Col>
            </Row>

            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Status">
                <ComplianceStatusBadge kind="tenant_lifecycle" value={selected.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Legal hold">
                <LegalHoldBadge active={selected.legal_hold_active} />
              </Descriptions.Item>
              <Descriptions.Item label="Active employees">
                {selected.active_employees}
              </Descriptions.Item>
              <Descriptions.Item label="Payroll records">
                {yesNoTag(selected.has_payroll_records)}
              </Descriptions.Item>
              <Descriptions.Item label="Open billing">
                {yesNoTag(selected.has_open_billing)}
              </Descriptions.Item>
            </Descriptions>

            {/* Simple status timeline note */}
            <Alert
              type="info"
              showIcon
              message="Lifecycle flow"
              description="ACTIVE → SUSPENDED → OFFBOARDING → RETENTION_REVIEW → RETENTION_LOCKED → ANONYMIZED. Each step is reason-gated and reversible until anonymization, which stays behind a feature flag."
            />

            {/* Blocking-dependency warnings */}
            {anonymizationBlocked && (
              <Alert
                type="warning"
                showIcon
                message="Anonymization & deletion are blocked"
                description={
                  <>
                    This tenant has {blockers.join(', ')}. Anonymization and deletion are blocked
                    until these dependencies are cleared
                    {selected.legal_hold_active ? ' and the legal hold is released' : ''}.
                  </>
                }
              />
            )}

            {/* Reason-gated transitions */}
            {canUpdate ? (
              <Card size="small" title="Lifecycle actions">
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {TRANSITIONS.map((spec) => (
                    <Button
                      key={spec.key}
                      block
                      danger={spec.danger}
                      icon={spec.icon}
                      onClick={() => setPending(spec)}
                    >
                      {spec.label}
                    </Button>
                  ))}

                  <Tooltip title="Requires retention review + no legal hold + feature flag">
                    <Button block disabled icon={<LockOutlined />}>
                      Schedule anonymization
                    </Button>
                  </Tooltip>

                  <Tooltip title="Hard deletion is disabled — behind a feature flag">
                    <Button block danger disabled icon={<StopOutlined />}>
                      Final delete / anonymize
                    </Button>
                  </Tooltip>

                  <Tooltip title="Export not available in this release">
                    <Button block disabled icon={<DownloadOutlined />}>
                      Export tenant data
                    </Button>
                  </Tooltip>
                </Space>
              </Card>
            ) : (
              <Alert
                type="info"
                showIcon
                message="Read-only"
                description="You can view this tenant's lifecycle but do not have permission to perform transitions."
              />
            )}
          </Space>
        )}
      </Drawer>

      {/* Single reason-gated confirmation surface for the pending transition. */}
      <ConfirmDangerDialog
        open={!!pending}
        title={pending?.title || ''}
        description={pending?.description}
        warning={pending?.warning}
        confirmLabel={pending?.confirmLabel || 'Confirm'}
        requireReason
        loading={acting}
        onConfirm={(reason) => {
          if (pending) void runTransition(pending, reason);
        }}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

export default TenantLifecyclePage;
